import { action, observable, makeObservable } from 'mobx';
import { SellerEditForm } from 'modules/seller/forms';
import { BaseViewStore } from 'core/stores';
import { ApplicationStore } from 'platform/application/stores';
import { FormHelperService } from 'common/data';
import { formInvalidate, isSome, generateRandomString } from 'core/utils';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName, organizationTypes, sourceOfChange } from 'common/constants';
import _ from 'lodash';
import { ModalParams } from 'core/models';

class SellerEditViewStore extends BaseViewStore {
	isUpdateAction = isSome(this.rootStore.routerStore.routerState.params.id);
	organizationTypeId = null;
	@observable timezoneOptions = [];
	@observable tempTimezoneOptions = [];
	@observable pageTimezone = 1;
	@observable stateOptions = [];
	@observable tempStateOptions = [];
	@observable pageState = 1;
	@observable countryOptions = [];
	@observable tempCountryOptions = [];
	@observable pageCountry = 1;
	@observable infiniteSearchPhrase = null;
	@observable warehouseManagements = [];
	debouncedTimezoneSearch = null;
	debouncedStateSearch = null;
	debouncedCountrySearch = null;
	warehouses = [];
	@observable isTimezoneLoading = false;
	@observable isStateLoading = false;
	@observable isCountryLoading = false;
	@observable isHavingAPIKey = false;

	form = new SellerEditForm({
		onSuccess: async () => {
			this.createOrUpdateApplication();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.listStore = new ApplicationStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.formHelperService = new FormHelperService();
		this.applicationStore = new ApplicationStore(rootStore);
		this.initialize(rootStore);
	}

	@action async initialize() {
		this.suspend();
		this.form.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		this.form.reset();
		await Promise.all([this.getLookupOptions(['timezone', 'state', 'country']), this.getWarehouses()]);
		this.debouncedTimezoneSearch = _.debounce((searchPhrase, { action }) => {
			this.searchTimezoneAsync(searchPhrase, { action });
		}, 500);
		this.debouncedStateSearch = _.debounce((searchPhrase, { action }) => {
			this.searchStateAsync(searchPhrase, { action });
		}, 500);
		this.debouncedCountrySearch = _.debounce((searchPhrase, { action }) => {
			this.searchCountryAsync(searchPhrase, { action });
		}, 500);
		if (this.isUpdateAction) {
			await this.getApplication(this.rootStore.routerStore.routerState.params.id);

			if (this.listStore.item.apiKey != '') this.isHavingAPIKey = true;

			const additionalInfo = this.listStore.item.sellerOrganizationInfo;
			this.listStore.item.address = additionalInfo.address;
			this.listStore.item.contact = additionalInfo.contact;
			this.listStore.item.minimumInventory = additionalInfo.minimumInventory;
			this.formHelperService.updateForm(this.form, this.listStore.item, true);
			if (additionalInfo && additionalInfo.address && additionalInfo.address.coreCountry) {
				this.form.$('country.id').value = {
					value: additionalInfo.address.coreCountryId,
					label: additionalInfo.address.coreCountry.name,
				};
			}
			if (additionalInfo && additionalInfo.address && additionalInfo.address.coreCountryState) {
				this.form.$('state.id').value = {
					value: additionalInfo.address.coreCountryStateId,
					label: additionalInfo.address.coreCountryState.name,
				};
			}
			if (additionalInfo && additionalInfo.timezone) {
				this.form.$('timezone.id').value = { value: additionalInfo.timezoneId, label: additionalInfo.timezone.name };
			}
		} else {
			this.form.$('email').set('rules', 'required|email|string');
			this.form.$('password').set('rules', 'required|string|password');
			this.form.$('confirmPassword').set('rules', 'required|string|same:password');
		}
		this.setOrganizationTypeId();
		this.resume();
	}

	@action.bound async setOrganizationTypeId() {
		await this.lookupStore.find({ page: 1, rpp: 250 }, lookupRouteName.organizationType);
		this.organizationTypeId = _.find(this.lookupStore.data, organizationType => {
			return organizationType.abrv.toUpperCase() === organizationTypes.seller.toUpperCase();
		}).id;
	}

	@action.bound
	getWarehouses = async () => {
		this.warehouses = (await this.applicationStore.find({
			embed: ['warehouseOrganizationInfo'],
			rpp: 100,
			page: 1,
			isOrganizationActive: true,
			warehouseOnly: true,
		})).item;
		await this.prepareData();
	};

	@action.bound
	prepareData = async () => {
		_.each(this.warehouses, warehouse => {
			this.warehouseManagements.push({
				warehouseName: warehouse.identifier,
				customerId: null,
				clientId: null,
				clientSecret: null,
				warehouseOrganizationInfoId: warehouse.warehouseOrganizationInfo.id,
				warehouseOrganizationInfo: warehouse.warehouseOrganizationInfo,
			});
		});
	};

	@action.bound
	getLookupOptions = async lookups => {
		for await (var lookup of lookups) {
			await this.lookupStore.find({ page: 1, rpp: 250 }, lookupRouteName[lookup]);
			this[`${lookup}Options`] = _.map(this.lookupStore.data, item => {
				return { label: item.currency != null ? item.currency : item.name, value: item.id, extra: item };
			});
		}
	};

	@action async getApplication(id) {
		await this.listStore.getApplication(id, {
			embed: [
				'sellerOrganizationInfo',
				'sellerOrganizationInfo.address',
				'sellerOrganizationInfo.address.coreCountry',
				'sellerOrganizationInfo.address.coreCountryState',
				'sellerOrganizationInfo.contact',
				'sellerOrganizationInfo.timezone',
			],
		});
	}

	@action.bound
	async handleOnTimezoneMenuScroll() {
		if (this.timezoneOptions.length < this.pageTimezone * 15) return;
		this.isTimezoneLoading = true;
		this.pageTimezone++;
		await this.lookupStore.find(
			{ search: this.infiniteSearchPhrase, page: this.pageTimezone, rpp: 15 },
			lookupRouteName.timezone
		);
		this.tempTimezoneOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
		if (this.tempTimezoneOptions.length > 0) {
			this.timezoneOptions = this.timezoneOptions.concat(this.tempTimezoneOptions);
		}
		this.isTimezoneLoading = false;
	}

	@action.bound
	async handleOnStateMenuScroll() {
		if (this.stateOptions.length < this.pageState * 15) return;
		this.isStateLoading = true;
		this.pageState++;
		await this.lookupStore.find(
			{ search: this.infiniteSearchPhrase, page: this.pageState, rpp: 15 },
			lookupRouteName.state
		);
		this.tempStateOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
		if (this.tempStateOptions.length > 0) {
			this.stateOptions = this.stateOptions.concat(this.tempStateOptions);
		}
		this.isStateLoading = false;
	}

	@action.bound
	async handleOnCountryMenuScroll() {
		if (this.countryOptions.length < this.pageCountry * 15) return;
		this.isCountryLoading = true;
		this.pageCountry++;
		await this.lookupStore.find(
			{ search: this.infiniteSearchPhrase, page: this.pageCountry, rpp: 15 },
			lookupRouteName.country
		);
		this.tempCountryOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
		if (this.tempCountryOptions.length > 0) {
			this.countryOptions = this.countryOptions.concat(this.tempCountryOptions);
		}
		this.isCountryLoading = false;
	}

	@action.bound
	async onMenuClose(routeName) {
		this.pageTimezone = 1;
		this.pageState = 1;
		this.pageCountry = 1;
		this.infiniteSearchPhrase = null;
		await this.getLookupOptions(routeName);
	}

	@action.bound
	async searchTimezoneAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.pageTimezone = 1;
		}
		if (action === 'input-change') {
			this.isTimezoneLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.pageTimezone = 1;
			this.timezoneOptions = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 15,
				orderDirection: 'asc',
				searchFields: 'name',
			};
			await this.lookupStore.find(filter, lookupRouteName.timezone);
			const newOptions = [];
			_.forEach(this.lookupStore.data, item => {
				newOptions.push({ value: item.id, label: item.name, extra: item });
			});
			this.timezoneOptions = newOptions;
			this.isTimezoneLoading = false;
		}
	}

	@action.bound
	async searchStateAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.pageState = 1;
		}
		if (action === 'input-change') {
			this.isStateLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.pageState = 1;
			this.stateOptions = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 15,
				orderDirection: 'asc',
				searchFields: 'name',
			};
			await this.lookupStore.find(filter, lookupRouteName.state);
			const newOptions = [];
			_.forEach(this.lookupStore.data, item => {
				newOptions.push({ value: item.id, label: item.name, extra: item });
			});
			this.stateOptions = newOptions;
			this.isStateLoading = false;
		}
	}

	@action.bound
	async searchCountryAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.pageCountry = 1;
		}
		if (action === 'input-change') {
			this.isCountryLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.pageCountry = 1;
			this.countryOptions = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 15,
				orderDirection: 'asc',
				searchFields: 'name',
			};
			await this.lookupStore.find(filter, lookupRouteName.country);
			const newOptions = [];
			_.forEach(this.lookupStore.data, item => {
				newOptions.push({ value: item.id, label: item.name, extra: item });
			});
			this.countryOptions = newOptions;
			this.isCountryLoading = false;
		}
	}

	@action.bound
	onSelectChange = (selectedOption, field) => {
		field.value = selectedOption;
	};

	@action async createOrUpdateApplication() {
		this.suspend();
		const {
			identifier,
			applicationTitle,
			address: { address1, address2, address3, city, zipCode },
			minimumInventory,
			country,
			state,
			timezone,
			contact,
			email,
			password,
			confirmPassword,
			apiKey,
		} = this.form.values();

		let addressObj = {
			address1,
			address2,
			address3,
			city,
			zipCode,
			coreCountryStateId: state.id.value ? state.id.value : state.id,
			coreCountryId: country.id.value ? country.id.value : country.id,
		};

		var organization = {
			identifier,
			applicationTitle,
			organizationTypeId: this.organizationTypeId,
			apiKey,
			isActive: this.isUpdateAction ? this.listStore.item.isActive : true,
		};

		var organizationInfo = {
			minimumInventory,
			printPackingSlip: this.isUpdateAction ? this.listStore.item.sellerOrganizationInfo.printPackingSlip : true,
			partiallyFulfillMixedOrders: this.isUpdateAction
				? this.listStore.item.sellerOrganizationInfo.partiallyFulfillMixedOrders
				: true,
			address: addressObj,
			contact,
			timezoneId: timezone.id.value ? timezone.id.value : timezone.id,
		};

		if (!this.isUpdateAction) {
			organization = {
				...organization,

				membershipData: {
					email,
					password,
				},

				coreUserInfo: {
					piiAccess: true,
				},

				confirmPassword,
			};
		} else {
			const additionalInfo = this.listStore.item.sellerOrganizationInfo;
			organizationInfo = {
				...organizationInfo,
				id: additionalInfo.id,
				addressId: additionalInfo.address.id,
				contactId: additionalInfo.contact.id,
			};
		}

		organization.sellerOrganizationInfo = organizationInfo;
		if (
			!this.isUpdateAction &&
			password
				.trim()
				.toLowerCase()
				.includes(
					email
						.split('@')[0]
						.toLowerCase()
						.trim()
				)
		) {
			this.form.invalidate('PASSWORDCONTAINSUNSERNAME');
		} else {
			try {
				if (this.isUpdateAction) {
					this.listStore.updateApplication(
						{ id: this.rootStore.routerStore.routerState.params.id, ...organization },
						sourceOfChange.editSeller
					);
				} else {
					organization.warehouseManagements = this.warehouseManagements;
					await this.listStore.createApplication(organization, sourceOfChange.createSeller);
					this.rootStore.notificationStore.warning(['SELLERVIEW.ADDREQUIREDCARRIER']);
					this.rootStore.notificationStore.warning(['SELLERVIEW.ADDSELLERWAREHOUSES']);
					this.rootStore.viewStore.sellerDropdownStore.fetchOptions();
				}
				this.rootStore.notificationStore.success(
					this.isUpdateAction
						? ['LOOKUPUPDATE.SELLER', { seller: organization.identifier }]
						: ['LOOKUPCREATE.SELLER', { seller: organization.identifier }]
				);
				this.rootStore.routerStore.goTo('master.app.main.seller.list');
			} catch (err) {
				formInvalidate({ form: this.form, err });
			}
		}
		this.resume();
	}

	@action.bound generateApiKey() {
		this.isHavingAPIKey = true;
		var radnomStringGenerated = generateRandomString(32);
		this.form.$('apiKey').set('value', radnomStringGenerated);
	}

	openEditSeller3PLCredentialsModal = () => {
		this.selectedItem = this.listStore.item;
		this.editSeller3PLCredentialsModal.open();
	};

	openEditSellerWarehousesModal = () => {
		this.selectedItem = this.listStore.item;
		this.editSellerWarehousesModal.open();
	};

	closeEditSeller3PLCredentialsModal = () => {
		this.editSeller3PLCredentialsModal.close();
	};

	closeEditSellerWarehousesModal = () => {
		this.editSellerWarehousesModal.close();
	};

	editSeller3PLCredentialsModal = new ModalParams();

	editSellerWarehousesModal = new ModalParams();
}

export default SellerEditViewStore;



// WEBPACK FOOTER //
// ./src/modules/seller/stores/SellerEditViewStore.js